import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import ContactForm from "../components/heider/ContactForm";
import Footer from "../components/heider/Footer";
import useReveal from "../hooks/useReveal";
import { Play, Pause, ArrowRight } from "lucide-react";

import { regions } from "../mock";

const Marquee = ({ items, reverse }) => (
  <div className="overflow-hidden py-2">
    <div className="marquee-track" style={reverse ? { animationDirection: "reverse" } : {}}>
      {[...items, ...items].map((name, i) => (
        <span key={i} className="flex-shrink-0 mx-8 md:mx-12 font-serif-display text-2xl md:text-4xl text-white/45 whitespace-nowrap">{name}</span>
      ))}
    </div>
  </div>
);

const DevelopersPage = () => {
  useReveal();
  const vidRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const v = vidRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };

  return (
    <div className="page-in">
      {/* Hero */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden bg-black">
        <img src="https://images.unsplash.com/photo-1638284457192-27d3d0ec51aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=2200" alt="Developers" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center text-white px-6 max-w-5xl mx-auto">
          <p className="text-[11px] tracking-luxe uppercase text-white/80 mb-6 fade-in" style={{ opacity: 0, animationDelay: "0.4s" }}>Developers</p>
          <h1 className="font-serif-display font-light text-4xl md:text-6xl lg:text-7xl leading-tight clip-line">
            <span style={{ opacity: 0 }}>We work with the best builders in Washington DC &amp; Northern VA</span>
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-[#f3f1ec] py-24 md:py-32">
        <div className="max-w-[900px] mx-auto px-6 text-center reveal">
          <p className="font-serif-display text-2xl md:text-[2rem] leading-relaxed text-[#2a2a2a] font-light">
            Capital Corner Group is the in-house sales partner developers trust when it matters most &mdash; from ground-up estates to landmark restorations. We deliver record prices and record pace on the region&rsquo;s most ambitious projects.
          </p>
        </div>

        {/* Featured before/after video */}
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 mt-20">
          <div className="flex items-end justify-between mb-8 reveal-left">
            <div>
              <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-3">Featured Project</p>
              <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#1a1a1a]">932 Frenchway &mdash; Before &amp; After</h2>
            </div>
          </div>
          <div className="reveal-scale relative overflow-hidden bg-black aspect-video group shadow-[0_40px_90px_rgba(0,0,0,0.18)]">
            <video ref={vidRef} className="w-full h-full object-cover" poster="/frenchway-poster.jpg" loop playsInline onClick={toggle}>
              <source src="/frenchway.mp4" type="video/mp4" />
            </video>
            <button onClick={toggle} className="absolute inset-0 flex items-center justify-center" aria-label="Play">
              <span className={`w-20 h-20 rounded-full bg-white/90 text-black flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${playing ? "opacity-0" : "opacity-100"}`}>
                <Play size={26} className="ml-1" />
              </span>
            </button>
            {playing && (
              <button onClick={toggle} className="absolute bottom-5 right-5 w-11 h-11 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors" aria-label="Pause">
                <Pause size={18} />
              </button>
            )}
          </div>
          <p className="text-neutral-500 text-[13px] mt-4 text-center">A full transformation delivered with our developer partners &mdash; from raw build to market-ready masterpiece.</p>
        </div>
      </section>

      {/* Builder marquee */}
      <section className="relative overflow-hidden bg-[#111113] text-white py-24 md:py-32">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center reveal">
          <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-6">Where We Work</p>
          <h2 className="font-serif-display text-4xl md:text-5xl font-light">Trusted Across the Capital Region</h2>
          <div className="w-40 h-px bg-[#b4936a] mx-auto my-10" />
        </div>
        <div className="space-y-3" data-testid="regions-marquee">
          <Marquee items={regions} />
          <Marquee items={regions.slice().reverse()} reverse />
        </div>
        <div className="text-center mt-16 reveal">
          <Link to="/sell" className="inline-flex items-center gap-2 text-[11px] tracking-luxe uppercase text-white border border-white/40 px-8 py-4 hover:bg-white hover:text-black transition-colors">
            Partner With Us <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <ContactForm />
      <Footer />
    </div>
  );
};

export default DevelopersPage;
